import getEnv from '@/utils/env';
import { App, ref } from 'vue';

const locale = ref('en');
const messages = ref();

export interface I18nMessages {
  [key: string]: string | I18nMessages;
}

export interface I18nOptions {
  locale: string;
  messages: { [key: string]: string };
}

export interface IOptions {
  params?: Array<string | number | undefined>;
  fallback?: string;
}

export const useI18n = () => {
  function translateLabel(
    label: string,
    params?: Array<string | number | undefined> | Record<string, string | number>
  ): string {
    if (label.includes('{') && params) {
      if (Array.isArray(params)) {
        // Map array values to named placeholders if they exist
        const namedPlaceholders = label.match(/\{(\w+)\}/g);
        if (namedPlaceholders) {
          namedPlaceholders.forEach((placeholder: string, index: number) => {
            if (params[index] !== undefined) {
              label = label.replace(placeholder, String(params[index]));
            }
          });
        } else {
          params.forEach((param, index) => {
            label = label.replace(`{${index}}`, `${param}`);
          });
        }
      } else {
        Object.entries(params).forEach(([key, value]) => {
          label = label.replace(`{${key}}`, String(value));
        });
      }
    }
    return label;
  }

  function t(key: string, params?: Array<string | number | undefined> | Record<string, string | number>) {
    if (!key) {
      if (getEnv('VITE_APP_ENV') === 'local') {
        console.error('key is not provided');
      }
      return '';
    }
    if (!messages.value) {
      /**
       * In case we are local and no refdata is loaded we return the key
       * This allows the unit test to access & find the keys.
       */
      return getEnv('VITE_APP_ENV') === 'local' ? key : 'n/a';
    }

    const label =
      key.split('.').reduce((o, i) => {
        if (o) return o[i];
      }, messages.value[locale.value]) ?? key;

    return translateLabel(label, params);
  }

  function tOptions(key: string, options?: IOptions) {
    if (!key) {
      if (getEnv('VITE_APP_ENV') === 'local') {
        console.error('key is not provided');
      }
      return '';
    }
    const { params, fallback } = options ?? {};

    const label =
      key.split('.').reduce((o, i) => {
        if (o) return o[i];
      }, messages.value[locale.value]) ??
      fallback ??
      key;

    return translateLabel(label, params);
  }

  function setLocaleMessage(lang: string, msgs: I18nMessages) {
    messages.value = {
      [lang.substring(0, 2)]: msgs
    };
  }
  function setLocale(loc: string) {
    locale.value = loc.substring(0, 2);
  }
  function getCurrentLocale() {
    return locale.value;
  }

  return {
    t,
    tOptions,
    getCurrentLocale,
    setLocale,
    setLocaleMessage
  };
};

export default {
  install: (app: App, options?: I18nOptions) => {
    if (options) {
      useI18n().setLocaleMessage(options.locale, options.messages);
    }
    app.config.globalProperties.$t = (key: string, params?: Array<string>) => useI18n().t(key, params);
  }
};
